import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useCatalog } from '../../../context/CatalogContext';
import { hasAnySeasonStats } from '../../../lib/seasonStatsPresence';

/* The loud gap.

   Individual missing marks get the small amber cell; this gets the big
   treatment — and only while it is actually missing, so exactly one item on the
   screen is ever this loud. Ported from `seasonStatsGap` in RecruitMeView.swift. */

const ChartGlyph = () => (
  <svg className="me-gap__icon" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <rect x="3" y="12" width="4" height="9" rx="1" />
    <rect x="10" y="7" width="4" height="14" rx="1" />
    <rect x="17" y="3" width="4" height="18" rx="1" />
  </svg>
);

/**
 * Graduation year against the ACADEMIC year — July or later is already the next
 * school year, so a senior graduating next June is "senior year" now.
 */
export function classYearLabel(graduationYear, now = new Date()) {
  if (!graduationYear) return 'season stats';
  const academicYear = now.getMonth() + 1 >= 7 ? now.getFullYear() + 1 : now.getFullYear();
  const delta = Number(graduationYear) - academicYear;
  if (delta < 0) return 'final season';
  return { 0: 'senior year', 1: 'junior year', 2: 'sophomore year' }[delta] || 'season stats';
}

/**
 * A swimmer has no passing yards. Football is named ONLY for football athletes;
 * every other sport gets its ledger's language or a neutral line.
 */
export function statsPrompt(statsMode, sportSlug) {
  if (statsMode === 'event_marks') return 'Your best marks by event — the first thing a coach scans after film.';
  if (statsMode === 'result_entries') return 'Meet results and finishes — the first thing a coach scans after film.';
  if (statsMode === 'match_record') return 'Your match record — the first thing a coach scans after film.';
  if (sportSlug === 'football') return 'Passing yards, TDs, completion % — the first thing a coach scans after film.';
  return 'Your season numbers — the first thing a coach scans after film.';
}

export default function SeasonStatsGap({ profile }) {
  const { API_URL } = useAuth();
  const navigate = useNavigate();
  const catalog = useCatalog();

  // Starts true so the loud block cannot flash during the first load and then
  // vanish. Only a definite `false` turns it on.
  const [hasStats, setHasStats] = useState(true);

  const sportSlug = catalog.sportSlugFor(profile?.sport);
  const statsMode = catalog.statsModeFor(sportSlug);

  // Re-check when the sport changes: the ledger is chosen by stats_mode, so the
  // previous answer doesn't carry over.
  useEffect(() => {
    if (!profile?.sport) return undefined;
    let cancelled = false;
    hasAnySeasonStats({ apiUrl: API_URL, sportSlug, statsMode }).then((res) => {
      // null means the lookup failed — leave the current state alone rather
      // than flipping the block on because the network blipped.
      if (!cancelled && res !== null) setHasStats(res);
    });
    return () => { cancelled = true; };
  }, [API_URL, sportSlug, statsMode, profile?.sport]);

  if (hasStats) return null;

  return (
    <button
      type="button"
      className="me-gap"
      onClick={() => navigate('/stats')}
      data-testid="recruit_me_season_stats_gap"
    >
      <ChartGlyph />
      <span className="me-gap__body">
        <span className="me-gap__eyebrow">Season Stats</span>
        <span className="me-gap__title">Add your {classYearLabel(profile?.graduation_year)}</span>
        <span className="me-gap__message">{statsPrompt(statsMode, sportSlug)}</span>
      </span>
      <span className="me-gap__cta">Start</span>
    </button>
  );
}
